import { useState, useEffect, useCallback } from 'react';
import { analyticsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  IndianRupee, TrendingUp, TrendingDown, Users, ShoppingBag, Factory,
  Scissors, Package, Calendar, Download, RefreshCw, AlertTriangle,
  ChevronRight, ArrowUpRight, ArrowDownRight, Clock, Heart, MessageSquare,
  Gift, Star, MapPin, Loader2, BarChart3, PieChart as PieChartIcon, FileText,
} from 'lucide-react';
import {
  exportToCSV, generatePrintHTML, printHTML, generateSummaryCardsHTML,
  generateTableHTML, formatCurrency, formatPercent,
} from '@/lib/exportUtils';
import { HelpTooltip } from '@/components/HelpTooltip';

// Brand colors
const COLORS = {
  navy: '#1B2A4A',
  gold: '#C5A55A',
  green: '#059669',
  red: '#DC2626',
  blue: '#2563EB',
  purple: '#7C3AED',
  orange: '#D97706',
  gray: '#6B7280',
};

const CHART_COLORS = [COLORS.navy, COLORS.gold, COLORS.blue, COLORS.green, COLORS.purple, COLORS.orange];

const DATE_RANGE_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const TABS = [
  { key: 'revenue', label: 'Revenue & Financial', icon: IndianRupee },
  { key: 'clients', label: 'Client Analytics', icon: Users },
  { key: 'production', label: 'Production', icon: Factory },
  { key: 'fabric', label: 'Fabric & Product', icon: Package },
  { key: 'engagement', label: 'Engagement', icon: Heart },
];

export default function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('revenue');
  const [dateRange, setDateRange] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleExport = () => {
    toast.success('Export feature coming soon');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.success('Analytics refreshed');
    setTimeout(() => setRefreshing(false), 1000);
  };

  const isAdmin = user?.role === 'Admin';

  return (
    <div className="p-6 space-y-6" data-testid="analytics-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: COLORS.navy }}>Analytics Dashboard</h1>
          <HelpTooltip text="Key business metrics and trends. Use tabs to explore revenue, clients, production, and fabric analytics." testId="help-analytics" />
          <p className="text-sm" style={{ color: COLORS.gray }}>
            Business intelligence and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date Range Picker */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[160px] h-9" data-testid="date-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dateRange === 'custom' && (
            <>
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                className="w-[140px] h-9" data-testid="custom-start-date" />
              <span className="text-sm" style={{ color: COLORS.gray }}>to</span>
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                className="w-[140px] h-9" data-testid="custom-end-date" />
            </>
          )}
          <Button variant="outline" size="sm" className="h-9 gap-1" onClick={handleRefresh}
            disabled={refreshing} data-testid="refresh-analytics-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1" onClick={handleExport}
            data-testid="export-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          // Sales can only see production and engagement tabs
          if (!isAdmin && !['production', 'engagement'].includes(tab.key)) return null;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative"
              style={{
                color: isActive ? COLORS.navy : COLORS.gray,
                borderBottom: isActive ? `2px solid ${COLORS.navy}` : '2px solid transparent',
              }}
              data-testid={`analytics-tab-${tab.key}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'revenue' && isAdmin && (
        <RevenueTab dateRange={dateRange} customStart={customStart} customEnd={customEnd} refreshKey={refreshing} />
      )}
      {activeTab === 'clients' && isAdmin && (
        <ClientsTab dateRange={dateRange} customStart={customStart} customEnd={customEnd} refreshKey={refreshing} />
      )}
      {activeTab === 'production' && (
        <ProductionTab dateRange={dateRange} customStart={customStart} customEnd={customEnd} refreshKey={refreshing} />
      )}
      {activeTab === 'fabric' && isAdmin && (
        <FabricTab dateRange={dateRange} customStart={customStart} customEnd={customEnd} refreshKey={refreshing} />
      )}
      {activeTab === 'engagement' && (
        <EngagementTab dateRange={dateRange} customStart={customStart} customEnd={customEnd} refreshKey={refreshing} />
      )}
    </div>
  );
}

/* ============================================================
   METRIC CARD COMPONENT
   ============================================================ */
function MetricCard({ title, value, subtitle, change, changeType, icon: Icon, color = COLORS.navy, loading }) {
  if (loading) return <Skeleton className="h-28 rounded-xl" />;
  
  const isPositive = changeType === 'positive' || (change && change > 0);
  const isNegative = changeType === 'negative' || (change && change < 0);
  
  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium mb-1" style={{ color: COLORS.gray }}>{title}</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.navy }}>{value}</p>
            {subtitle && <p className="text-xs mt-1" style={{ color: COLORS.gray }}>{subtitle}</p>}
            {change !== undefined && change !== null && (
              <div className="flex items-center gap-1 mt-1">
                {isPositive && <ArrowUpRight className="w-3 h-3" style={{ color: COLORS.green }} />}
                {isNegative && <ArrowDownRight className="w-3 h-3" style={{ color: COLORS.red }} />}
                <span className="text-xs font-medium" style={{ color: isPositive ? COLORS.green : isNegative ? COLORS.red : COLORS.gray }}>
                  {change > 0 ? '+' : ''}{change}%
                </span>
                <span className="text-[10px]" style={{ color: COLORS.gray }}>vs prev period</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   TAB 1: REVENUE & FINANCIAL
   ============================================================ */
function RevenueTab({ dateRange, customStart, customEnd, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [byGarment, setByGarment] = useState([]);
  const [byTier, setByTier] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [byStylist, setByStylist] = useState([]);
  const [vendorSpend, setVendorSpend] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const start = dateRange === 'custom' ? customStart : undefined;
    const end = dateRange === 'custom' ? customEnd : undefined;
    
    Promise.all([
      analyticsAPI.getRevenueSummary(dateRange, start, end),
      analyticsAPI.getRevenueTrend(dateRange, 'daily', start, end),
      analyticsAPI.getRevenueByGarmentType(dateRange, start, end),
      analyticsAPI.getRevenueByFabricTier(dateRange, start, end),
      analyticsAPI.getRevenueByClientSource(dateRange, start, end),
      analyticsAPI.getRevenueByStylist(dateRange, start, end),
      analyticsAPI.getVendorSpend(dateRange, start, end),
      analyticsAPI.getCashFlowForecast(),
    ]).then(([sumRes, trendRes, garmentRes, tierRes, sourceRes, stylistRes, vendorRes, cashRes]) => {
      setSummary(sumRes.data);
      setTrend(trendRes.data?.data || []);
      setByGarment(garmentRes.data?.data || []);
      setByTier(tierRes.data?.data || []);
      setBySource(sourceRes.data?.data || []);
      setByStylist(stylistRes.data?.data || []);
      setVendorSpend(vendorRes.data?.data || []);
      setCashFlow(cashRes.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dateRange, customStart, customEnd, refreshKey]);

  const formatCurrency = (val) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val?.toLocaleString('en-IN') || 0}`;
  };

  const handleExportCSV = () => {
    const rows = [
      { metric: 'Total Revenue', value: summary?.total_revenue || 0 },
      { metric: 'Order Count', value: summary?.order_count || 0 },
      { metric: 'Average Order Value', value: summary?.avg_order_value || 0 },
      { metric: 'Total Collected', value: summary?.total_collected || 0 },
      { metric: 'Total Outstanding', value: summary?.total_outstanding || 0 },
    ];
    const success = exportToCSV(rows, [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' },
    ], `revenue-summary-${new Date().toISOString().slice(0,10)}`);
    if (success) toast.success('Revenue summary exported');
  };

  const handleExportPDF = () => {
    const sections = [
      generateSummaryCardsHTML([
        { label: 'Total Revenue', value: formatCurrency(summary?.total_revenue) },
        { label: 'Orders', value: summary?.order_count || 0 },
        { label: 'Avg Order Value', value: formatCurrency(summary?.avg_order_value) },
        { label: 'Outstanding', value: formatCurrency(summary?.total_outstanding) },
      ]),
      generateTableHTML('Revenue by Garment Type', [
        { key: 'garment_type', label: 'Garment' },
        { key: 'revenue', label: 'Revenue', align: 'right', format: 'currency' },
        { key: 'count', label: 'Orders', align: 'right' },
      ], byGarment),
      generateTableHTML('Revenue by Fabric Tier', [
        { key: 'tier', label: 'Tier' },
        { key: 'revenue', label: 'Revenue', align: 'right', format: 'currency' },
        { key: 'percentage', label: '%', align: 'right', format: 'percent' },
      ], byTier),
    ];
    const html = generatePrintHTML('Revenue & Financial Report', sections);
    printHTML(html);
    toast.success('Opening PDF export...');
  };

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1 text-xs" disabled={loading}>
          <Download className="w-3 h-3" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1 text-xs" disabled={loading}>
          <FileText className="w-3 h-3" /> Export PDF
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard title="Total Revenue" value={formatCurrency(summary?.total_revenue)} 
          change={summary?.revenue_growth} icon={IndianRupee} color={COLORS.green} loading={loading} />
        <MetricCard title="Orders" value={summary?.order_count} 
          subtitle="in period" icon={ShoppingBag} color={COLORS.navy} loading={loading} />
        <MetricCard title="Avg Order Value" value={formatCurrency(summary?.avg_order_value)} 
          icon={TrendingUp} color={COLORS.gold} loading={loading} />
        <MetricCard title="Collected" value={formatCurrency(summary?.total_collected)} 
          icon={IndianRupee} color={COLORS.blue} loading={loading} />
        <MetricCard title="Outstanding" value={formatCurrency(summary?.total_outstanding)} 
          icon={AlertTriangle} color={COLORS.red} loading={loading} />
      </div>

      {/* Revenue Trend Chart */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
            <TrendingUp className="w-4 h-4" style={{ color: COLORS.gold }} />
            Revenue Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-64" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.gold} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke={COLORS.gold} strokeWidth={2} fill="url(#revenueGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Breakdown Charts */}
      <div className="grid grid-cols-3 gap-4">
        {/* By Garment Type */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold" style={{ color: COLORS.navy }}>
              By Garment Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={byGarment.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <YAxis type="category" dataKey="garment_type" tick={{ fontSize: 9 }} width={70} />
                  <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill={COLORS.navy} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By Fabric Tier */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold" style={{ color: COLORS.navy }}>
              By Fabric Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={byTier} dataKey="revenue" nameKey="tier" cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                    paddingAngle={2} label={({ tier, percent }) => `${tier} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {byTier.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By Client Source */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold" style={{ color: COLORS.navy }}>
              By Client Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={bySource} dataKey="revenue" nameKey="source" cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                    paddingAngle={2} label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {bySource.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Stylist / Salesperson */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="revenue-by-stylist-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
            <Users className="w-4 h-4" style={{ color: COLORS.gold }} />
            Revenue by Stylist / Salesperson
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-48" /> : byStylist.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--ink-muted)' }}>No stylist data available</p>
          ) : (
            <div className="space-y-3">
              {byStylist.map((s, i) => {
                const maxRevenue = Math.max(...byStylist.map(x => x.revenue), 1);
                const barWidth = (s.revenue / maxRevenue) * 100;
                return (
                  <div key={s.stylist_id || i} className="flex items-center gap-3" data-testid={`stylist-row-${s.stylist_id}`}>
                    <div className="w-24 text-sm font-medium truncate" style={{ color: COLORS.navy }}>
                      {s.stylist_name || 'Unknown'}
                    </div>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full flex items-center justify-end px-2"
                        style={{ 
                          width: `${barWidth}%`, 
                          backgroundColor: i === 0 ? COLORS.gold : COLORS.navy + (i % 2 === 0 ? 'CC' : '99'),
                          minWidth: '60px',
                        }}
                      >
                        <span className="text-[10px] font-semibold text-white whitespace-nowrap">
                          ₹{(s.revenue/1000).toFixed(0)}K
                        </span>
                      </div>
                    </div>
                    <div className="w-20 text-right">
                      <p className="text-xs font-semibold" style={{ color: COLORS.navy }}>{s.order_count} orders</p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>AOV ₹{(s.avg_order_value/1000).toFixed(1)}K</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cash Flow Forecast & Vendor Spend */}
      <div className="grid grid-cols-2 gap-4">
        {/* Cash Flow */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <BarChart3 className="w-4 h-4" style={{ color: COLORS.blue }} />
              30-Day Cash Flow Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={cashFlow.filter((_, i) => i % 3 === 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <Tooltip />
                  <Area type="monotone" dataKey="running_balance" stroke={COLORS.blue} fill={COLORS.blue + '30'} name="Balance" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Vendor Spend */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Package className="w-4 h-4" style={{ color: COLORS.purple }} />
              Vendor Spend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={vendorSpend.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                  <YAxis type="category" dataKey="vendor" tick={{ fontSize: 9 }} width={80} />
                  <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Spend']} />
                  <Bar dataKey="spend" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   TAB 2: CLIENT ANALYTICS
   ============================================================ */
function ClientsTab({ dateRange, customStart, customEnd, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [clvRanking, setClvRanking] = useState([]);
  const [segmentation, setSegmentation] = useState(null);
  const [locations, setLocations] = useState(null);
  const [referralNetwork, setReferralNetwork] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const start = dateRange === 'custom' ? customStart : undefined;
    const end = dateRange === 'custom' ? customEnd : undefined;
    
    Promise.all([
      analyticsAPI.getClientSummary(dateRange, start, end),
      analyticsAPI.getClientCLVRanking(20),
      analyticsAPI.getClientSegmentation(),
      analyticsAPI.getClientLocationDistribution(),
      analyticsAPI.getReferralNetwork(),
    ]).then(([sumRes, clvRes, segRes, locRes, refRes]) => {
      setSummary(sumRes.data);
      setClvRanking(clvRes.data?.data || []);
      setSegmentation(segRes.data);
      setLocations(locRes.data);
      setReferralNetwork(refRes.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dateRange, customStart, customEnd, refreshKey]);

  const segmentData = segmentation?.distribution ? [
    { name: 'VIP', value: segmentation.distribution.vip || 0, color: COLORS.gold },
    { name: 'Regular', value: segmentation.distribution.regular || 0, color: COLORS.green },
    { name: 'Occasional', value: segmentation.distribution.occasional || 0, color: COLORS.blue },
    { name: 'New', value: segmentation.distribution.new || 0, color: COLORS.purple },
    { name: 'Dormant', value: segmentation.distribution.dormant || 0, color: COLORS.red },
  ] : [];

  const handleExportCSV = () => {
    const success = exportToCSV(clvRanking, [
      { key: 'client_name', label: 'Client' },
      { key: 'total_orders', label: 'Orders' },
      { key: 'lifetime_value', label: 'LTV (₹)' },
      { key: 'avg_order_value', label: 'AOV (₹)' },
    ], `client-clv-ranking-${new Date().toISOString().slice(0,10)}`);
    if (success) toast.success('CLV ranking exported');
  };

  const handleExportPDF = () => {
    const sections = [
      generateSummaryCardsHTML([
        { label: 'Total Clients', value: summary?.total_clients || 0 },
        { label: 'New This Period', value: summary?.new_clients || 0 },
        { label: 'VIP Clients', value: segmentation?.distribution?.vip || 0 },
        { label: 'Dormant', value: segmentation?.distribution?.dormant || 0 },
      ]),
      generateTableHTML('Client Lifetime Value Ranking', [
        { key: 'client_name', label: 'Client' },
        { key: 'total_orders', label: 'Orders', align: 'right' },
        { key: 'lifetime_value', label: 'LTV', align: 'right', format: 'currency' },
      ], clvRanking),
    ];
    const html = generatePrintHTML('Client Analytics Report', sections);
    printHTML(html);
    toast.success('Opening PDF export...');
  };

  return (
    <div className="space-y-6" data-testid="clients-tab">
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1 text-xs" disabled={loading}>
          <Download className="w-3 h-3" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1 text-xs" disabled={loading}>
          <FileText className="w-3 h-3" /> Export PDF
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard title="Total Clients" value={summary?.total_clients} 
          icon={Users} color={COLORS.navy} loading={loading} />
        <MetricCard title="New This Period" value={summary?.new_clients} 
          icon={TrendingUp} color={COLORS.green} loading={loading} />
        <MetricCard title="VIP Clients" value={segmentation?.distribution?.vip || 0} 
          subtitle={`Revenue ≥ ₹${((segmentation?.vip_threshold || 0) / 1000).toFixed(0)}K`}
          icon={Star} color={COLORS.gold} loading={loading} />
        <MetricCard title="Dormant (Re-engage)" value={segmentation?.distribution?.dormant || 0} 
          subtitle="No orders in 12+ months"
          icon={AlertTriangle} color={COLORS.red} loading={loading} />
      </div>

      {/* Segmentation & CLV */}
      <div className="grid grid-cols-2 gap-4">
        {/* Segmentation Pie */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <PieChartIcon className="w-4 h-4" style={{ color: COLORS.blue }} />
              Client Segmentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-64" /> : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={segmentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                    {segmentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* CLV Ranking Table */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Star className="w-4 h-4" style={{ color: COLORS.gold }} />
              Client Lifetime Value Ranking
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? <Skeleton className="h-64 m-4" /> : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr style={{ borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                      <th className="text-left p-2 font-medium" style={{ color: COLORS.gray }}>Client</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Revenue</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Orders</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Days Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clvRanking.map((c, i) => (
                      <tr key={c.client_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid rgba(27,42,74,0.04)' }}>
                        <td className="p-2">
                          <span className="font-medium" style={{ color: COLORS.navy }}>{c.client_name}</span>
                        </td>
                        <td className="p-2 text-right font-medium" style={{ color: COLORS.green }}>
                          ₹{(c.total_revenue / 1000).toFixed(0)}K
                        </td>
                        <td className="p-2 text-right" style={{ color: COLORS.gray }}>{c.total_orders}</td>
                        <td className="p-2 text-right">
                          <Badge variant="outline" className="text-[9px]" style={{
                            color: c.days_since_last_order > 365 ? COLORS.red : c.days_since_last_order > 90 ? COLORS.orange : COLORS.green,
                          }}>
                            {c.days_since_last_order}d
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Location & Referral */}
      <div className="grid grid-cols-2 gap-4">
        {/* Location Distribution */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <MapPin className="w-4 h-4" style={{ color: COLORS.red }} />
              Client Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: COLORS.gray }}>By Country</p>
                  <div className="flex flex-wrap gap-2">
                    {(locations?.by_country || []).map(loc => (
                      <Badge key={loc.country} variant="outline" className="text-xs">
                        {loc.country}: {loc.count}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: COLORS.gray }}>Top Cities</p>
                  <ResponsiveContainer width="100%" height={120}>
                    <BarChart data={(locations?.by_city || []).slice(0, 5)} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 9 }} />
                      <YAxis type="category" dataKey="city" tick={{ fontSize: 9 }} width={60} />
                      <Bar dataKey="count" fill={COLORS.navy} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dormant Clients (Re-engagement List) */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <AlertTriangle className="w-4 h-4" style={{ color: COLORS.red }} />
              Re-Engagement Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? <Skeleton className="h-48 m-4" /> : (
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr style={{ borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                      <th className="text-left p-2 font-medium" style={{ color: COLORS.gray }}>Client</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Total Revenue</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Last Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(segmentation?.dormant_clients || []).slice(0, 10).map(c => (
                      <tr key={c.client_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid rgba(27,42,74,0.04)' }}>
                        <td className="p-2">
                          <span className="font-medium" style={{ color: COLORS.navy }}>{c.client_name || 'Unknown'}</span>
                        </td>
                        <td className="p-2 text-right" style={{ color: COLORS.green }}>
                          ₹{(c.total_revenue / 1000).toFixed(0)}K
                        </td>
                        <td className="p-2 text-right" style={{ color: COLORS.gray }}>
                          {c.last_order?.slice(0, 10) || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   TAB 3: PRODUCTION ANALYTICS
   ============================================================ */
function ProductionTab({ dateRange, customStart, customEnd, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [workerProductivity, setWorkerProductivity] = useState([]);
  const [productionTime, setProductionTime] = useState([]);
  const [alterationStats, setAlterationStats] = useState(null);
  const [capacityUtil, setCapacityUtil] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const start = dateRange === 'custom' ? customStart : undefined;
    const end = dateRange === 'custom' ? customEnd : undefined;
    
    Promise.all([
      analyticsAPI.getProductionSummary(dateRange, start, end),
      analyticsAPI.getWorkerProductivity(dateRange, start, end),
      analyticsAPI.getAvgProductionTime(dateRange, start, end),
      analyticsAPI.getAlterationStats(dateRange, start, end),
      analyticsAPI.getCapacityUtilisation(),
    ]).then(([sumRes, workerRes, timeRes, altRes, capRes]) => {
      setSummary(sumRes.data);
      setWorkerProductivity(workerRes.data?.data || []);
      setProductionTime(timeRes.data?.data || []);
      setAlterationStats(altRes.data);
      setCapacityUtil(capRes.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dateRange, customStart, customEnd, refreshKey]);

  const handleExportCSV = () => {
    const rows = [
      { metric: 'Total Orders', value: summary?.total_orders || 0 },
      { metric: 'Delivered Orders', value: summary?.delivered_orders || 0 },
      { metric: 'On-Time Rate %', value: summary?.on_time_rate || 0 },
      { metric: 'Alteration Rate %', value: summary?.alteration_rate || 0 },
      { metric: 'Total Items', value: summary?.total_items || 0 },
      { metric: 'Total Alterations', value: summary?.alterations || 0 },
    ];
    // Add worker productivity data
    workerProductivity.forEach(w => {
      rows.push({ metric: `Worker: ${w.worker_name}`, value: `${w.tasks_completed} tasks` });
    });
    const success = exportToCSV(rows, [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' },
    ], `production-analytics-${new Date().toISOString().slice(0,10)}`);
    if (success) toast.success('Production data exported');
  };

  const handleExportPDF = () => {
    const sections = [
      generateSummaryCardsHTML([
        { label: 'Total Orders', value: summary?.total_orders || 0 },
        { label: 'Delivered', value: summary?.delivered_orders || 0 },
        { label: 'On-Time Rate', value: `${summary?.on_time_rate || 0}%` },
        { label: 'Alteration Rate', value: `${summary?.alteration_rate || 0}%` },
      ]),
      generateTableHTML('Worker Productivity', [
        { key: 'worker_name', label: 'Worker' },
        { key: 'tasks_completed', label: 'Tasks', align: 'right' },
        { key: 'avg_time', label: 'Avg Time (days)', align: 'right' },
      ], workerProductivity),
      generateTableHTML('Avg Production Time by Garment', [
        { key: 'garment_type', label: 'Garment' },
        { key: 'avg_days', label: 'Avg Days', align: 'right' },
      ], productionTime),
    ];
    const html = generatePrintHTML('Production Analytics Report', sections);
    printHTML(html);
    toast.success('Opening PDF export...');
  };

  return (
    <div className="space-y-6" data-testid="production-tab">
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1 text-xs" disabled={loading}>
          <Download className="w-3 h-3" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1 text-xs" disabled={loading}>
          <FileText className="w-3 h-3" /> Export PDF
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard title="Total Orders" value={summary?.total_orders} 
          icon={ShoppingBag} color={COLORS.navy} loading={loading} />
        <MetricCard title="Delivered" value={summary?.delivered_orders} 
          icon={Package} color={COLORS.green} loading={loading} />
        <MetricCard title="On-Time Rate" value={`${summary?.on_time_rate || 0}%`} 
          changeType={summary?.on_time_rate >= 90 ? 'positive' : 'negative'}
          icon={Clock} color={COLORS.blue} loading={loading} />
        <MetricCard title="Alteration Rate" value={`${summary?.alteration_rate || 0}%`} 
          subtitle={`${summary?.alterations || 0} alterations`}
          changeType={summary?.alteration_rate <= 20 ? 'positive' : 'negative'}
          icon={Scissors} color={COLORS.purple} loading={loading} />
        <MetricCard title="Total Items" value={summary?.total_items} 
          icon={Factory} color={COLORS.gold} loading={loading} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-2 gap-4">
        {/* Worker Productivity */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Users className="w-4 h-4" style={{ color: COLORS.blue }} />
              Worker Productivity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={workerProductivity.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="worker_name" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="tasks_completed" fill={COLORS.navy} radius={[4, 4, 0, 0]} name="Tasks Completed" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Capacity Utilisation */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <BarChart3 className="w-4 h-4" style={{ color: COLORS.gold }} />
              Capacity Utilisation (12 weeks)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={capacityUtil}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="week" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 9 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v) => [`${v}%`, 'Utilisation']} />
                  <Area type="monotone" dataKey="utilisation" stroke={COLORS.gold} fill={COLORS.gold + '30'} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-2 gap-4">
        {/* Avg Production Time */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Clock className="w-4 h-4" style={{ color: COLORS.green }} />
              Avg Production Time by Garment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={productionTime} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 9 }} unit=" days" />
                  <YAxis type="category" dataKey="garment_type" tick={{ fontSize: 9 }} width={70} />
                  <Tooltip formatter={(v) => [`${v} days`, 'Avg Time']} />
                  <Bar dataKey="avg_days" fill={COLORS.green} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top Alteration Zones */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Scissors className="w-4 h-4" style={{ color: COLORS.purple }} />
              Most Common Alterations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={(alterationStats?.top_alteration_zones || []).slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 9 }} />
                  <YAxis type="category" dataKey="zone" tick={{ fontSize: 9 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="count" fill={COLORS.purple} radius={[0, 4, 4, 0]} name="Frequency" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   TAB 4: FABRIC & PRODUCT ANALYTICS
   ============================================================ */
function FabricTab({ dateRange, customStart, customEnd, refreshKey }) {
  const [bestSelling, setBestSelling] = useState([]);
  const [tierMix, setTierMix] = useState([]);
  const [slowMoving, setSlowMoving] = useState([]);
  const [garmentPopularity, setGarmentPopularity] = useState([]);
  const [seasonalTrends, setSeasonalTrends] = useState({ data: [], garment_types: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const start = dateRange === 'custom' ? customStart : undefined;
    const end = dateRange === 'custom' ? customEnd : undefined;
    
    Promise.all([
      analyticsAPI.getBestSellingFabrics(dateRange, start, end, 10),
      analyticsAPI.getFabricTierMix(dateRange, start, end),
      analyticsAPI.getSlowMovingFabrics(90),
      analyticsAPI.getGarmentTypePopularity(dateRange, start, end),
      analyticsAPI.getSeasonalTrends(),
    ]).then(([bestRes, tierRes, slowRes, popRes, seasonRes]) => {
      setBestSelling(bestRes.data?.data || []);
      setTierMix(tierRes.data?.data || []);
      setSlowMoving(slowRes.data?.data || []);
      setGarmentPopularity(popRes.data?.data || []);
      setSeasonalTrends(seasonRes.data || { data: [], garment_types: [] });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dateRange, customStart, customEnd, refreshKey]);

  const handleExportCSV = () => {
    const success = exportToCSV(bestSelling, [
      { key: 'fabric_code', label: 'Fabric Code' },
      { key: 'mill_name', label: 'Mill' },
      { key: 'metres', label: 'Metres Ordered' },
      { key: 'revenue', label: 'Revenue (₹)' },
    ], `fabric-analytics-${new Date().toISOString().slice(0,10)}`);
    if (success) toast.success('Fabric data exported');
  };

  const handleExportPDF = () => {
    const sections = [
      generateSummaryCardsHTML([
        { label: 'Best Seller', value: bestSelling[0]?.fabric_code || '-' },
        { label: 'Slow Moving', value: `${slowMoving.length} fabrics` },
        { label: 'Top Garment', value: garmentPopularity[0]?.garment_type || '-' },
        { label: 'Premium Mix', value: `${tierMix.find(t => t.tier === 'premium')?.percentage || 0}%` },
      ]),
      generateTableHTML('Best Selling Fabrics', [
        { key: 'fabric_code', label: 'Fabric' },
        { key: 'mill_name', label: 'Mill' },
        { key: 'metres', label: 'Metres', align: 'right' },
        { key: 'revenue', label: 'Revenue', align: 'right', format: 'currency' },
      ], bestSelling),
      generateTableHTML('Garment Type Popularity', [
        { key: 'garment_type', label: 'Garment' },
        { key: 'count', label: 'Orders', align: 'right' },
        { key: 'percentage', label: '%', align: 'right', format: 'percent' },
      ], garmentPopularity),
      generateTableHTML('Slow Moving Fabrics (90+ days)', [
        { key: 'internal_code', label: 'Fabric' },
        { key: 'stock_metres', label: 'Stock (m)', align: 'right' },
        { key: 'price_per_metre', label: 'Price/m', align: 'right', format: 'currency' },
      ], slowMoving.slice(0, 10)),
    ];
    const html = generatePrintHTML('Fabric & Product Analytics Report', sections);
    printHTML(html);
    toast.success('Opening PDF export...');
  };

  return (
    <div className="space-y-6" data-testid="fabric-tab">
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1 text-xs" disabled={loading}>
          <Download className="w-3 h-3" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1 text-xs" disabled={loading}>
          <FileText className="w-3 h-3" /> Export PDF
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard title="Best Seller" value={bestSelling[0]?.fabric_code || '-'} 
          subtitle={`${bestSelling[0]?.metres || 0}m ordered`}
          icon={Star} color={COLORS.gold} loading={loading} />
        <MetricCard title="Slow Moving" value={slowMoving.length} 
          subtitle="No orders in 90+ days"
          icon={AlertTriangle} color={COLORS.red} loading={loading} />
        <MetricCard title="Top Garment" value={garmentPopularity[0]?.garment_type || '-'} 
          subtitle={`${garmentPopularity[0]?.percentage || 0}% of orders`}
          icon={Package} color={COLORS.navy} loading={loading} />
        <MetricCard title="Premium Mix" value={`${tierMix.find(t => t.tier === 'premium')?.percentage || 0}%`} 
          subtitle="of orders use premium fabrics"
          icon={Star} color={COLORS.purple} loading={loading} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-2 gap-4">
        {/* Best Selling Fabrics */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Star className="w-4 h-4" style={{ color: COLORS.gold }} />
              Best Selling Fabrics
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? <Skeleton className="h-64 m-4" /> : bestSelling.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: COLORS.gray }}>No order data yet</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr style={{ borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                      <th className="text-left p-2 font-medium" style={{ color: COLORS.gray }}>Fabric</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Metres</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bestSelling.map((f, i) => (
                      <tr key={f.fabric_id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid rgba(27,42,74,0.04)' }}>
                        <td className="p-2">
                          <span className="font-medium" style={{ color: COLORS.navy }}>{f.fabric_code}</span>
                          <span className="text-[10px] ml-1" style={{ color: COLORS.gray }}>{f.mill_name}</span>
                        </td>
                        <td className="p-2 text-right" style={{ color: COLORS.gray }}>{f.metres.toFixed(1)}m</td>
                        <td className="p-2 text-right font-medium" style={{ color: COLORS.green }}>
                          ₹{(f.revenue / 1000).toFixed(0)}K
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Garment Type Popularity */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <PieChartIcon className="w-4 h-4" style={{ color: COLORS.blue }} />
              Garment Type Popularity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : garmentPopularity.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: COLORS.gray }}>No order data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={garmentPopularity} dataKey="count" nameKey="garment_type" cx="50%" cy="50%" outerRadius={80}
                    label={({ garment_type, percentage }) => `${garment_type} ${percentage}%`} labelLine={false}>
                    {garmentPopularity.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fabric Tier Mix & Slow Moving */}
      <div className="grid grid-cols-2 gap-4">
        {/* Tier Mix */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Package className="w-4 h-4" style={{ color: COLORS.purple }} />
              Fabric Tier Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : tierMix.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: COLORS.gray }}>No order data yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={tierMix}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="tier" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip formatter={(v) => [v, 'Items']} />
                  <Bar dataKey="count" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Slow Moving Fabrics */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <AlertTriangle className="w-4 h-4" style={{ color: COLORS.red }} />
              Slow Moving Fabrics (90+ days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? <Skeleton className="h-48 m-4" /> : slowMoving.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: COLORS.gray }}>No slow-moving fabrics</p>
            ) : (
              <div className="max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr style={{ borderBottom: '1px solid rgba(27,42,74,0.08)' }}>
                      <th className="text-left p-2 font-medium" style={{ color: COLORS.gray }}>Fabric</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Stock</th>
                      <th className="text-right p-2 font-medium" style={{ color: COLORS.gray }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slowMoving.slice(0, 10).map(f => (
                      <tr key={f.id} className="hover:bg-gray-50" style={{ borderBottom: '1px solid rgba(27,42,74,0.04)' }}>
                        <td className="p-2">
                          <span className="font-medium" style={{ color: COLORS.navy }}>{f.internal_code}</span>
                        </td>
                        <td className="p-2 text-right" style={{ color: COLORS.gray }}>{f.stock_metres || 0}m</td>
                        <td className="p-2 text-right" style={{ color: COLORS.gray }}>₹{f.price_per_metre || 0}/m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================
   TAB 5: ENGAGEMENT ANALYTICS
   ============================================================ */
function EngagementTab({ dateRange, customStart, customEnd, refreshKey }) {
  const [summary, setSummary] = useState(null);
  const [notificationBreakdown, setNotificationBreakdown] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [lookbookStats, setLookbookStats] = useState(null);
  const [postDelivery, setPostDelivery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const start = dateRange === 'custom' ? customStart : undefined;
    const end = dateRange === 'custom' ? customEnd : undefined;
    
    Promise.all([
      analyticsAPI.getEngagementSummary(dateRange, start, end),
      analyticsAPI.getNotificationBreakdown(dateRange, start, end),
      analyticsAPI.getReferralProgramStats(dateRange, start, end),
      analyticsAPI.getLookbookCampaignStats(dateRange, start, end),
      analyticsAPI.getPostDeliveryStats(dateRange, start, end),
    ]).then(([sumRes, notifRes, refRes, lookRes, postRes]) => {
      setSummary(sumRes.data);
      setNotificationBreakdown(notifRes.data);
      setReferralStats(refRes.data);
      setLookbookStats(lookRes.data);
      setPostDelivery(postRes.data?.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [dateRange, customStart, customEnd, refreshKey]);

  const handleExportCSV = () => {
    const rows = [
      { metric: 'Notifications Sent', value: summary?.notifications_sent || 0 },
      { metric: 'Appointments Booked', value: summary?.appointments_booked || 0 },
      { metric: 'Appointment Conversion %', value: summary?.appointment_conversion || 0 },
      { metric: 'Lookbook Cards Sent', value: summary?.lookbook_cards_sent || 0 },
      { metric: 'Lookbook Revenue', value: summary?.lookbook_revenue || 0 },
      { metric: 'Total Referrals', value: referralStats?.total_referrals || 0 },
      { metric: 'Referred Orders', value: referralStats?.referred_orders || 0 },
      { metric: 'Referred Revenue', value: referralStats?.referred_revenue || 0 },
      { metric: 'Rewards Cost', value: referralStats?.rewards_cost || 0 },
      { metric: 'Program ROI %', value: referralStats?.roi || 0 },
    ];
    const success = exportToCSV(rows, [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' },
    ], `engagement-analytics-${new Date().toISOString().slice(0,10)}`);
    if (success) toast.success('Engagement data exported');
  };

  const handleExportPDF = () => {
    const sections = [
      generateSummaryCardsHTML([
        { label: 'Notifications', value: summary?.notifications_sent || 0 },
        { label: 'Appointments', value: summary?.appointments_booked || 0 },
        { label: 'Lookbook Cards', value: summary?.lookbook_cards_sent || 0 },
        { label: 'Referrals', value: referralStats?.total_referrals || 0 },
      ]),
      `<h2>Referral Program Performance</h2>
      <div class="summary-grid">
        <div class="summary-card"><div class="summary-label">Total Referrals</div><div class="summary-value">${referralStats?.total_referrals || 0}</div></div>
        <div class="summary-card"><div class="summary-label">Referred Orders</div><div class="summary-value">${referralStats?.referred_orders || 0}</div></div>
        <div class="summary-card"><div class="summary-label">Referred Revenue</div><div class="summary-value">₹${((referralStats?.referred_revenue || 0) / 1000).toFixed(0)}K</div></div>
        <div class="summary-card"><div class="summary-label">Program ROI</div><div class="summary-value">${referralStats?.roi || 0}%</div></div>
      </div>`,
      `<h2>Lookbook Campaign Performance</h2>
      <div class="summary-grid">
        <div class="summary-card"><div class="summary-label">Campaigns</div><div class="summary-value">${lookbookStats?.campaigns_count || 0}</div></div>
        <div class="summary-card"><div class="summary-label">Cards Sent</div><div class="summary-value">${lookbookStats?.cards_sent || 0}</div></div>
        <div class="summary-card"><div class="summary-label">Enquiry Rate</div><div class="summary-value">${lookbookStats?.enquiry_rate || 0}%</div></div>
        <div class="summary-card"><div class="summary-label">Conversion Rate</div><div class="summary-value">${lookbookStats?.conversion_rate || 0}%</div></div>
      </div>`,
      generateTableHTML('Notifications by Channel', [
        { key: 'channel', label: 'Channel' },
        { key: 'count', label: 'Count', align: 'right' },
      ], notificationBreakdown?.by_channel || []),
    ];
    const html = generatePrintHTML('Engagement Analytics Report', sections);
    printHTML(html);
    toast.success('Opening PDF export...');
  };

  return (
    <div className="space-y-6" data-testid="engagement-tab">
      {/* Export Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1 text-xs" disabled={loading}>
          <Download className="w-3 h-3" /> Export CSV
        </Button>
        <Button variant="outline" size="sm" onClick={handleExportPDF} className="gap-1 text-xs" disabled={loading}>
          <FileText className="w-3 h-3" /> Export PDF
        </Button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard title="Notifications Sent" value={summary?.notifications_sent || 0} 
          icon={MessageSquare} color={COLORS.blue} loading={loading} />
        <MetricCard title="Appointments" value={summary?.appointments_booked || 0} 
          subtitle={`${summary?.appointment_conversion || 0}% completed`}
          icon={Calendar} color={COLORS.navy} loading={loading} />
        <MetricCard title="Lookbook Cards" value={summary?.lookbook_cards_sent || 0} 
          subtitle={`₹${((summary?.lookbook_revenue || 0) / 1000).toFixed(0)}K revenue`}
          icon={Heart} color={COLORS.gold} loading={loading} />
        <MetricCard title="Referrals" value={referralStats?.total_referrals || 0} 
          subtitle={`${referralStats?.roi || 0}% ROI`}
          icon={Gift} color={COLORS.green} loading={loading} />
        <MetricCard title="Referred Revenue" value={`₹${((referralStats?.referred_revenue || 0) / 1000).toFixed(0)}K`} 
          icon={IndianRupee} color={COLORS.purple} loading={loading} />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-2 gap-4">
        {/* Notification Breakdown */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <MessageSquare className="w-4 h-4" style={{ color: COLORS.blue }} />
              Notifications by Channel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={notificationBreakdown?.by_channel || []} dataKey="count" nameKey="channel" 
                    cx="50%" cy="50%" outerRadius={70}
                    label={({ channel, count }) => `${channel}: ${count}`} labelLine={false}>
                    {(notificationBreakdown?.by_channel || []).map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Lookbook Campaign Performance */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Heart className="w-4 h-4" style={{ color: COLORS.gold }} />
              Lookbook Campaign Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.navy + '08' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.navy }}>{lookbookStats?.campaigns_count || 0}</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Campaigns</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.gold + '15' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.gold }}>{lookbookStats?.cards_sent || 0}</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Cards Sent</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.blue + '08' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.blue }}>{lookbookStats?.enquiry_rate || 0}%</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Enquiry Rate</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.green + '08' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.green }}>{lookbookStats?.conversion_rate || 0}%</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Conversion</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: COLORS.gray }}>Total Revenue Attributed</span>
                    <span className="text-sm font-bold" style={{ color: COLORS.green }}>
                      ₹{((lookbookStats?.total_revenue || 0) / 1000).toFixed(0)}K
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Referral & Post-Delivery */}
      <div className="grid grid-cols-2 gap-4">
        {/* Referral Program */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Gift className="w-4 h-4" style={{ color: COLORS.green }} />
              Referral Program ROI
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.green + '08' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.green }}>{referralStats?.total_referrals || 0}</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Referrals</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.navy + '08' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.navy }}>{referralStats?.referred_orders || 0}</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Orders</p>
                  </div>
                  <div className="text-center p-2 rounded-lg" style={{ backgroundColor: COLORS.gold + '15' }}>
                    <p className="text-lg font-bold" style={{ color: COLORS.gold }}>{referralStats?.rewards_redeemed || 0}</p>
                    <p className="text-[10px]" style={{ color: COLORS.gray }}>Rewards</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs" style={{ color: COLORS.gray }}>Revenue from Referrals</span>
                    <span className="text-sm font-bold" style={{ color: COLORS.green }}>
                      ₹{((referralStats?.referred_revenue || 0) / 1000).toFixed(0)}K
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs" style={{ color: COLORS.gray }}>Rewards Cost</span>
                    <span className="text-sm font-medium" style={{ color: COLORS.red }}>
                      -₹{((referralStats?.rewards_cost || 0) / 1000).toFixed(0)}K
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid rgba(27,42,74,0.08)' }}>
                    <span className="text-xs font-medium" style={{ color: COLORS.navy }}>Program ROI</span>
                    <span className="text-sm font-bold" style={{ color: referralStats?.roi > 0 ? COLORS.green : COLORS.red }}>
                      {referralStats?.roi || 0}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Post-Delivery Engagement */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: COLORS.navy }}>
              <Heart className="w-4 h-4" style={{ color: COLORS.purple }} />
              Post-Delivery Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-48" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={postDelivery}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="type" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Bar dataKey="scheduled" fill={COLORS.gray} name="Scheduled" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="sent" fill={COLORS.green} name="Sent" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
